/**
 * DimensionComparisonChart — Horizontal bar comparison
 *
 * For each dimension: icon + label, two bars (neutral vs thesis weight),
 * and the dimension score badge. Highlights over/under-weighted dims.
 * Built with View + percentage widths (no charting library).
 */

import React from 'react';
import { View, Text } from 'react-native';
import {
  Users, Lightbulb, Target, TrendingUp, Shield, Swords,
  DollarSign, Clock, Sparkles, Megaphone, Wallet, Zap, Scale,
  BarChart3,
} from 'lucide-react-native';
import Animated, { FadeInDown } from 'react-native-reanimated';
import { useThemeColors } from '@/lib/useThemeColors';
import { DIMENSION_ORDER } from '@/lib/scoring/methodology';
import type { ThesisDimensionDetail } from '@/lib/thesis/types';

// Icon map (same as investor-score-detail)
const DIMENSION_ICONS: Record<string, typeof TrendingUp> = {
  team: Users,
  product: Lightbulb,
  market: Target,
  traction: TrendingUp,
  defensibility: Shield,
  competition: Swords,
  'business-model': DollarSign,
  timing: Clock,
  'vision-story': Sparkles,
  'gtm-execution': Megaphone,
  'financial-health': Wallet,
  'execution-velocity': Zap,
  scalability: Scale,
};

interface DimensionComparisonChartProps {
  details: ThesisDimensionDetail[];
  colors: ReturnType<typeof useThemeColors>;
}

export function DimensionComparisonChart({
  details,
  colors,
}: DimensionComparisonChartProps) {
  // Map details by dimension for ordering
  const detailMap = new Map(details.map((d) => [d.dimension, d]));

  // Max weight for bar scaling (cap at 20%)
  const maxWeight = 0.20;

  return (
    <View className="mt-3 gap-1.5">
      {/* Legend */}
      <View className="flex-row items-center gap-4 mb-2 px-1">
        <View className="flex-row items-center gap-1.5">
          <View className="w-3 h-2 rounded-sm" style={{ backgroundColor: colors.light[400] }} />
          <Text style={{ color: colors.text.tertiary }} className="text-xs">
            Neutral (1/N)
          </Text>
        </View>
        <View className="flex-row items-center gap-1.5">
          <View className="w-3 h-2 rounded-sm" style={{ backgroundColor: colors.primary[500] }} />
          <Text style={{ color: colors.text.tertiary }} className="text-xs">
            Your Thesis
          </Text>
        </View>
      </View>

      {DIMENSION_ORDER.map((dim, index) => {
        const detail = detailMap.get(dim);
        if (!detail) return null;

        const Icon = DIMENSION_ICONS[dim] || BarChart3;
        const isOverWeight = detail.weightDelta > 0.005;
        const isUnderWeight = detail.weightDelta < -0.005;
        const neutralPct = (detail.neutralWeight / maxWeight) * 100;
        const thesisPct = (detail.thesisWeight / maxWeight) * 100;

        return (
          <Animated.View
            key={dim}
            entering={FadeInDown.delay(60 + index * 30).duration(300)}
          >
            <View
              className="rounded-lg px-3 py-2.5"
              style={{
                backgroundColor: isOverWeight
                  ? `${colors.primary[500]}08`
                  : colors.surface.primary,
              }}
            >
              {/* Dimension header row */}
              <View className="flex-row items-center gap-2 mb-1.5">
                <Icon
                  size={13}
                  color={isOverWeight ? colors.primary[600] : colors.text.tertiary}
                />
                <Text
                  style={{
                    color: isOverWeight ? colors.primary[700] : colors.text.primary,
                    fontWeight: isOverWeight ? '600' : '500',
                  }}
                  className="text-xs flex-1"
                >
                  {detail.label}
                </Text>
                {/* Weight percentage */}
                <Text
                  style={{
                    color: isOverWeight
                      ? colors.primary[600]
                      : isUnderWeight
                        ? colors.text.tertiary
                        : colors.text.secondary,
                    fontVariant: ['tabular-nums'],
                  }}
                  className="text-xs font-semibold"
                >
                  {(detail.thesisWeight * 100).toFixed(1)}%
                </Text>
                {/* Score badge */}
                <View
                  className="px-1.5 py-0.5 rounded"
                  style={{ backgroundColor: colors.surface.secondary }}
                >
                  <Text
                    style={{ color: colors.text.secondary, fontVariant: ['tabular-nums'] }}
                    className="text-xs font-bold"
                  >
                    {detail.score}
                  </Text>
                </View>
              </View>

              {/* Bars */}
              <View className="gap-1">
                {/* Neutral bar */}
                <View
                  className="h-1.5 rounded-full overflow-hidden"
                  style={{ backgroundColor: colors.surface.secondary }}
                >
                  <View
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.min(100, neutralPct)}%`,
                      backgroundColor: colors.light[400],
                    }}
                  />
                </View>

                {/* Thesis bar */}
                <View
                  className="h-1.5 rounded-full overflow-hidden"
                  style={{ backgroundColor: colors.surface.secondary }}
                >
                  <View
                    className="h-full rounded-full"
                    style={{
                      width: `${Math.min(100, thesisPct)}%`,
                      backgroundColor: isOverWeight
                        ? colors.primary[500]
                        : isUnderWeight
                          ? colors.light[500]
                          : colors.primary[400],
                    }}
                  />
                </View>
              </View>
            </View>
          </Animated.View>
        );
      })}
    </View>
  );
}
